// src/components/Admin/ManageBlogPosts.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import Overlay from '../Overlay';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage2';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';


function ManageBlogPosts() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [formState, setFormState] = useState({
    title: '',
    author: '',
    content: '',
    imageFile: null,
    imageURL: '',
    id: null, // 編集時に使用
  });
  const [preview, setPreview] = useState(null); // プレビュー用
  const [showOverlay, setShowOverlay] = useState(false);

  // トリミング用の状態
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const snapshot = await db.collection("blogPosts").orderBy("date", "desc").get();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBlogPosts(data);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      alert('ブログ投稿の取得に失敗しました。');
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormState(prev => ({ ...prev, imageFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setIsCropping(true); // トリミングモードに切り替え
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setIsCropping(false);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const { blob, fileUrl } = await getCroppedImg(preview, croppedAreaPixels, zoom, 1.5, 300, 200); // aspect=1.5 (300/200)
      setCroppedImage(blob);
      setPreview(fileUrl); // プレビューをトリミング後の画像に更新
      setIsCropping(false);
    } catch (e) {
      console.error(e);
      alert('画像のトリミングに失敗しました。');
    }
  }, [preview, croppedAreaPixels, zoom]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (formState.id) {
      await updateBlogPost();
    } else {
      await addBlogPost();
    }
  };

  const addBlogPost = async () => {
    try {
      if (!croppedImage) {
        alert('画像をアップロードしてトリミングしてください。');
        return;
      }

      const storageRef = storage.ref(`blogPosts/${Date.now()}_${formState.imageFile.name}`);
      await storageRef.put(croppedImage);
      const imageURL = await storageRef.getDownloadURL();

      await db.collection("blogPosts").add({
        title: formState.title,
        author: formState.author,
        content: formState.content,
        imageURL,
        date: new Date(),
      });

      alert('ブログ投稿が追加されました。');
      resetForm();
      fetchBlogPosts();
    } catch (error) {
      console.error("Error adding blog post:", error);
      alert('ブログ投稿の追加に失敗しました。');
    }
  };

  const updateBlogPost = async () => {
    try {
      let imageURL = formState.imageURL;
      if (croppedImage) {
        const storageRef = storage.ref(`blogPosts/${Date.now()}_${formState.imageFile.name}`);
        await storageRef.put(croppedImage);
        imageURL = await storageRef.getDownloadURL();
      }

      await db.collection("blogPosts").doc(formState.id).update({
        title: formState.title,
        author: formState.author,
        content: formState.content,
        imageURL,
        // date: new Date(), // 更新時に日付を変更する場合はコメントを外す
      });

      alert('ブログ投稿が更新されました。');
      resetForm();
      setShowOverlay(false);
      fetchBlogPosts();
    } catch (error) {
      console.error("Error updating blog post:", error);
      alert('ブログ投稿の更新に失敗しました。');
    }
  };

  const deleteBlogPost = async (id, imageURL) => {
    if (!window.confirm('このブログ投稿を削除してもよろしいですか？')) return;
    try {
      await db.collection("blogPosts").doc(id).delete();

      if (imageURL) {
        const storageRef = storage.refFromURL(imageURL);
        await storageRef.delete();
      }

      alert('ブログ投稿が削除されました。');
      fetchBlogPosts();
    } catch (error) {
      console.error("Error deleting blog post:", error);
      alert('ブログ投稿の削除に失敗しました。');
    }
  };

  const editBlogPost = blogPost => {
    setFormState({
      title: blogPost.title,
      author: blogPost.author,
      content: blogPost.content,
      imageFile: null,
      imageURL: blogPost.imageURL || '',
      id: blogPost.id,
    });
    setPreview(blogPost.imageURL || null);
    setCroppedImage(null);
    setIsCropping(false);
  };

  const viewBlogPost = blogPost => {
    setFormState({
      title: blogPost.title,
      author: blogPost.author,
      content: blogPost.content,
      imageFile: null,
      imageURL: blogPost.imageURL || '',
      id: blogPost.id,
    });
    setShowOverlay(true);
  };

  const resetForm = () => {
    setFormState({
      title: '',
      author: '',
      content: '',
      imageFile: null,
      imageURL: '',
      id: null,
    });
    setPreview(null);
    setCroppedImage(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="manage-blogposts">
      <h2>ブログ投稿の管理</h2>
      <form onSubmit={handleSubmit}>
        <label>
          タイトル:
          <input 
            type="text" 
            name="title" 
            value={formState.title} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          著者:
          <input 
            type="text" 
            name="author" 
            value={formState.author} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          コンテンツ:
          <textarea 
            name="content" 
            value={formState.content} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          画像:
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
            required={!formState.id} // 編集時は必須ではない
          />
        </label>

        {isCropping && preview && (
          <div className="crop-container">
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={3 / 2} // 300 / 200 = 1.5
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="controls">
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom)}
                style={{ width: '200px' }}
              />
              <Button variant="contained" color="primary" onClick={showCroppedImage} style={{ marginLeft: '10px' }}>
                トリミング
              </Button>
            </div>
          </div>
        )}

        {preview && !isCropping && (
          <div className="preview">
            <p>プレビュー:</p>
            <img 
              src={preview} 
              alt="Preview" 
              style={{ 
                width: '300px', 
                height: '200px', 
                objectFit: 'cover', 
                border: '2px solid #ccc' 
              }} 
            />
          </div>
        )}

        <Button type="submit" variant="contained" color="primary" className="button">
          {formState.id ? 'ブログ投稿を更新' : 'ブログ投稿を追加'}
        </Button>
        {formState.id && (
          <Button 
            type="button" 
            variant="outlined" 
            color="secondary" 
            onClick={resetForm}
            className="button"
            style={{ marginLeft: '10px' }}
          >
            キャンセル
          </Button>
        )}
      </form>

      <h3>既存のブログ投稿</h3>
      <ul>
        {blogPosts.map(blogPost => (
          <li key={blogPost.id} className="admin-list-item">
            {blogPost.imageURL && <img src={blogPost.imageURL} alt={blogPost.title} style={{ width: '150px', height: '100px', objectFit: 'cover' }} />}
            <div>
              <h4>{blogPost.title}</h4>
              <p>著者: {blogPost.author}</p>
              <p>{blogPost.date.toDate().toLocaleDateString()}</p>
              <Button onClick={() => viewBlogPost(blogPost)} variant="outlined" color="primary" style={{ marginRight: '10px' }}>
                プレビュー
              </Button>
              <Button onClick={() => editBlogPost(blogPost)} variant="outlined" color="secondary" style={{ marginRight: '10px' }}>
                編集
              </Button>
              <Button onClick={() => deleteBlogPost(blogPost.id, blogPost.imageURL)} variant="outlined" color="error">
                削除
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {showOverlay && (
        <Overlay onClose={() => setShowOverlay(false)}>
          <h3>{formState.title}</h3>
          <p>著者: {formState.author}</p>
          <p>投稿日: {new Date(formState.date).toLocaleDateString()}</p>
          {formState.imageURL && <img src={formState.imageURL} alt={formState.title} style={{ width: '300px', height: '200px', objectFit: 'cover', marginBottom: '1rem' }} />}
          <p>{formState.content}</p>
        </Overlay>
      )}
    </div>
  );
}

export default ManageBlogPosts;
